import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, X, ExternalLink, Radar, Link2, MapPin, AlertTriangle, Trash2, CheckCircle2, Flag } from 'lucide-react';

const AdminTabParentQueue = () => {
  const [discovering, setDiscovering] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [priority, setPriority] = useState([]);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState({ total: 0, published_this_week: 0 });
  const [pcState, setPcState] = useState('');
  const [pcCounty, setPcCounty] = useState('');
  const [fixUrl, setFixUrl] = useState({});
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fl, pc, rec] = await Promise.all([
        axios.get(`${API_URL}/admin/parent-resources/flagged`),
        axios.get(`${API_URL}/admin/priority-counties`),
        axios.get(`${API_URL}/admin/parent-resources/recent?limit=50`),
      ]);
      setFlagged(fl.data.items || []);
      setPriority(pc.data.items || []);
      setRecent(rec.data.items || []);
      setStats({ total: rec.data.total || 0, published_this_week: rec.data.published_this_week || 0 });
    } catch (e) { toast.error('Failed to load parent resources'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const addPriority = async () => {
    if (!pcState.trim() || !pcCounty.trim()) { toast.error('Enter state and county'); return; }
    setBusy('pc-add');
    try {
      await axios.post(`${API_URL}/admin/priority-counties`, { state: pcState.trim(), county: pcCounty.trim() });
      toast.success(`${pcCounty}, ${pcState} added as a priority`);
      setPcState(''); setPcCounty('');
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); } finally { setBusy(null); }
  };
  const delPriority = async (id) => {
    try { await axios.delete(`${API_URL}/admin/priority-counties/${id}`); await loadAll(); } catch (e) { console.error('Delete priority county failed:', e); }
  };
  const resolveFlag = async (id) => {
    setBusy('res-' + id);
    try { await axios.post(`${API_URL}/admin/parent-resources/${id}/resolve`); toast.success('Marked fixed'); await loadAll(); }
    catch (e) { toast.error('Failed'); } finally { setBusy(null); }
  };
  const saveFixUrl = async (id) => {
    const url = fixUrl[id];
    if (!url) { toast.error('Enter a new link'); return; }
    setBusy('fix-' + id);
    try { await axios.put(`${API_URL}/admin/parent-resources/${id}`, { website: url }); toast.success('Link updated & flag cleared'); await loadAll(); }
    catch (e) { toast.error('Update failed'); } finally { setBusy(null); }
  };
  const unpublishRes = async (id) => {
    setBusy('unp-' + id);
    try { await axios.post(`${API_URL}/admin/parent-resources/${id}/unpublish`); toast.success('Unpublished'); await loadAll(); }
    catch (e) { toast.error('Failed'); } finally { setBusy(null); }
  };

  const runDiscovery = async () => {
    setDiscovering(true);
    try {
      const r = await axios.post(`${API_URL}/admin/parent-queue/run-discovery`, { states: 4 });
      toast.success(`Discovery published ${r.data.added} new resource(s)`);
      await loadAll();
    } catch (e) { toast.error('Discovery failed'); } finally { setDiscovering(false); }
  };

  const runLinkCheck = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API_URL}/admin/parent-resources/link-check`, { limit: 100 });
      if (r.data.broken > 0) toast.warning(`Checked ${r.data.checked} links — ${r.data.broken} not responding (flagged)`);
      else toast.success(`Checked ${r.data.checked} links — all responding`);
      await loadAll();
    } catch (e) { toast.error('Link check failed'); } finally { setChecking(false); }
  };

  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return ''; } };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="admin-parent-resources">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div>
          <h2 className="font-serif text-xl font-bold text-primary mb-1">Parent Resources</h2>
          <p className="text-sm text-gray-500">Verified resources publish automatically. Discovery runs weekly; broken links are checked monthly. Use the tools below to steer discovery and fix flagged links.</p>
        </div>
        <div className="flex gap-3">
          <div className="text-center bg-primary/5 rounded-xl px-4 py-2" data-testid="stat-total-published">
            <div className="text-2xl font-bold text-primary">{stats.total}</div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Published</div>
          </div>
          <div className="text-center bg-secondary/10 rounded-xl px-4 py-2" data-testid="stat-week-published">
            <div className="text-2xl font-bold text-secondary">{stats.published_this_week}</div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">This Week</div>
          </div>
        </div>
      </div>

      {/* Discovery + Link Check controls */}
      <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-4 my-5 flex flex-wrap items-center gap-3">
        <Button onClick={runDiscovery} disabled={discovering} className="h-9 bg-secondary hover:bg-secondary-hover text-white" data-testid="run-discovery-btn" title="Discover & auto-publish new resources nationwide (runs weekly automatically)">
          {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Radar className="w-4 h-4 mr-1" />Run Discovery Now</>}
        </Button>
        <Button onClick={runLinkCheck} disabled={checking} variant="outline" className="h-9" data-testid="link-check-btn" title="Check published resource links (runs monthly automatically)">
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-4 h-4 mr-1" />Check Links Now</>}
        </Button>
        <span className="text-xs text-gray-500">New finds go live immediately — no approval needed.</span>
      </div>

      {/* Priority Counties + Needs Review panels */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border border-gray-100 rounded-xl p-4" data-testid="priority-counties-panel">
          <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-sm"><MapPin className="w-4 h-4" />Priority Counties</div>
          <p className="text-xs text-gray-500 mb-3">Discovery searches these high-traffic areas aggressively every run.</p>
          <div className="flex gap-2 mb-3">
            <Input value={pcState} onChange={(e) => setPcState(e.target.value)} placeholder="State (e.g. Texas)" className="h-9" data-testid="pc-state" />
            <Input value={pcCounty} onChange={(e) => setPcCounty(e.target.value)} placeholder="County (e.g. Bell)" className="h-9" data-testid="pc-county" />
            <Button onClick={addPriority} disabled={busy === 'pc-add'} className="h-9 bg-primary text-white whitespace-nowrap" data-testid="pc-add-btn">Add</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {priority.length === 0 && <span className="text-xs text-gray-400">No priority counties yet.</span>}
            {priority.map((p) => (
              <span key={p.pc_id} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2 py-1 rounded-full" data-testid={`pc-item-${p.pc_id}`}>
                {p.county} County, {p.state}
                <button onClick={() => delPriority(p.pc_id)} className="hover:text-red-600" data-testid={`pc-del-${p.pc_id}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="border border-gray-100 rounded-xl p-4" data-testid="flagged-links-panel">
          <div className="flex items-center gap-2 mb-2 font-semibold text-sm">
            <AlertTriangle className={`w-4 h-4 ${flagged.length ? 'text-amber-500' : 'text-gray-300'}`} />
            <span className={flagged.length ? 'text-amber-600' : 'text-gray-500'}>Needs Review</span>
            {flagged.length > 0 && <span className="bg-amber-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid="needs-review-badge">{flagged.length}</span>}
          </div>
          <p className="text-xs text-gray-500 mb-3">Links flagged by the monthly check or reported by users. Fix the link or unpublish.</p>
          <div className="space-y-2 max-h-72 overflow-auto">
            {flagged.length === 0 && <span className="text-xs text-emerald-600">All links healthy ✓</span>}
            {flagged.map((r) => (
              <div key={r.resource_id} className="border border-amber-100 bg-amber-50/40 rounded-lg p-2" data-testid={`flagged-${r.resource_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-800">{r.name}</p>
                  {r.user_reported && <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full"><Flag className="w-2.5 h-2.5" />User reported{r.report_count > 1 ? ` ×${r.report_count}` : ''}</span>}
                  {r.link_status === 'broken' && <span className="text-[10px] font-bold bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full">Link down</span>}
                </div>
                {r.last_report_reason && <p className="text-[11px] text-red-600 italic mb-0.5">“{r.last_report_reason}”</p>}
                <p className="text-[11px] text-gray-500 mb-1 truncate">{r.website}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={fixUrl[r.resource_id] ?? ''} onChange={(e) => setFixUrl({ ...fixUrl, [r.resource_id]: e.target.value })} placeholder="New official link" className="h-8 text-xs flex-1 min-w-[160px]" data-testid={`fix-url-${r.resource_id}`} />
                  <Button size="sm" onClick={() => saveFixUrl(r.resource_id)} disabled={busy === 'fix-' + r.resource_id} className="h-8 bg-primary text-white" data-testid={`fix-save-${r.resource_id}`}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => resolveFlag(r.resource_id)} disabled={busy === 'res-' + r.resource_id} className="h-8" data-testid={`mark-fixed-${r.resource_id}`}>Mark OK</Button>
                  <Button size="sm" variant="outline" onClick={() => unpublishRes(r.resource_id)} disabled={busy === 'unp-' + r.resource_id} className="h-8 text-red-600 border-red-200" data-testid={`unpublish-${r.resource_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recently Published */}
      <div className="border border-gray-100 rounded-xl p-4" data-testid="recently-published-panel">
        <div className="flex items-center gap-2 mb-3 text-primary font-semibold text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Recently Published</div>
        {loading ? (
          <div className="p-6 text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : recent.length === 0 ? (
          <div className="p-6 text-center text-gray-400">No resources yet. Click "Run Discovery Now" to find and publish resources.</div>
        ) : (
          <div className="space-y-2 max-h-[28rem] overflow-auto">
            {recent.map((r) => (
              <div key={r.resource_id} className="flex items-start justify-between gap-3 border border-gray-100 rounded-lg p-3" data-testid={`recent-${r.resource_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{r.name}</span>
                    <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{r.parent_category_label}</span>
                    {r.state && <span className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{r.state}</span>}
                    {r.auto_found && <span className="text-[11px] bg-secondary/15 text-secondary px-2 py-0.5 rounded-full">auto</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-gray-500">
                    <span>src: {r.source}</span>
                    <span>{fmtDate(r.created_at)}</span>
                    {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1"><ExternalLink className="w-3 h-3" />visit</a>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => unpublishRes(r.resource_id)} disabled={busy === 'unp-' + r.resource_id} className="h-8 text-red-600 border-red-200 flex-shrink-0" data-testid={`recent-unpublish-${r.resource_id}`} title="Unpublish">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTabParentQueue;
